import React, { useCallback, useContext, useEffect, useState, useMemo, useRef } from "react";
import { Chip, Typography, IconButton, Tooltip } from "@mui/material";
import { Context } from "../../states/Store";
import { ERROR, SUCCESS } from "../../services/common/Constants";
import i18next from "i18next";
import {
  getAvailableReports,
  getMyJobs,
  generateReport,
  getDownloadUrl,
  deleteJob,
  getRecentJobs,
} from "../../services/edge/ReportService";
import EADataGrid from "../../components/common/datatable/EADataGrid";
import ListLayout from "../../components/common/ListLayout";
import ReportGenerationFlow from "./ReportGenerationFlow";
import ReportsEmptyState from "./ReportsEmptyState";
import moment from "moment";
import Icon from "@mui/material/Icon";

function ReportsDataTable() {
  const [state, dispatch] = useContext(Context);

  const [availableReports, setAvailableReports] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loader, setLoader] = useState(true);
  const [showGenerationFlow, setShowGenerationFlow] = useState(false);
  const intervalRef = useRef(null);

  const fetchAvailableReports = useCallback(() => {
    if (state.account?.id) {
      setLoader(true);
      (async function fetchData() {
        try {
          const resp = await getAvailableReports(state.account.id, dispatch);
          // Handle both array response and object with content property
          const reports = Array.isArray(resp) ? resp : (resp?.content || []);
          setAvailableReports(reports);
          setLoader(false);
        } catch (e) {
          const msg =
            (e?.message?.error && e?.message?.error[0]?.code) ||
            "INTERNAL_ERROR";
          dispatch({ type: "ALERT", alert: { type: ERROR, msg } });
          setLoader(false);
        }
      })();
    }
  }, [dispatch, state.account?.id]);

  const fetchJobs = useCallback(() => {
    if (state.account?.id) {
      (async function fetchData() {
        try {
          const resp = await getMyJobs(state.account.id, null, 0, 50, dispatch);
          // Handle both array response and paginated response
          const jobsList = Array.isArray(resp) ? resp : (resp?.content || []);
          setJobs(jobsList);
        } catch (e) {
          const msg =
            (e?.message?.error && e?.message?.error[0]?.code) ||
            "INTERNAL_ERROR";
          dispatch({ type: "ALERT", alert: { type: ERROR, msg } });
        }
      })();
    }
  }, [dispatch, state.account?.id]);

  // Reset and fetch data when account changes
  useEffect(() => {
    if (state.account?.id) {
      setJobs([]);
      setAvailableReports([]);
      setShowGenerationFlow(false);
      fetchAvailableReports();
      fetchJobs();
    }
  }, [state.account?.id, fetchAvailableReports, fetchJobs]);

  // Calculate if there are pending jobs
  const hasPendingJobs = useMemo(
    () => jobs.some((job) => job.status === "PENDING"),
    [jobs]
  );

  // Auto-refresh recent jobs every 2 seconds when there are pending jobs
  useEffect(() => {
    if (hasPendingJobs && state.account?.id && !intervalRef.current) {
      // Start polling only if not already running
      const interval = setInterval(async () => {
        try {
          // Fetch all recent jobs (last 5 minutes) in one call
          const recentJobs = await getRecentJobs(state.account.id, dispatch);
          const recentJobsList = Array.isArray(recentJobs) ? recentJobs : (recentJobs?.content || []);

          // Check if there are still pending jobs in the fetched data
          const hasPending = recentJobsList.some((job) => job.status === "PENDING");

          if (!hasPending && intervalRef.current) {
            // Stop polling immediately if no more pending jobs
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          // Update jobs list by merging recent jobs with existing ones
          setJobs((prevJobs) => {
            const jobsMap = new Map(prevJobs.map(j => [j.jobId, j]));

            // Update existing jobs with new data from recent jobs
            recentJobsList.forEach(recentJob => {
              jobsMap.set(recentJob.jobId, recentJob);
            });

            return Array.from(jobsMap.values());
          });
        } catch (e) {
          console.error("Error fetching recent jobs:", e);
        }
      }, 2000); // Check every 2 seconds
      intervalRef.current = interval;
    } else if (!hasPendingJobs && intervalRef.current) {
      // Stop polling when no more pending jobs
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      // Cleanup on unmount
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hasPendingJobs, state.account?.id, dispatch]);

  const handleGenerate = useCallback(() => {
    setShowGenerationFlow(true);
  }, []);

  const handleGenerationSubmit = useCallback(
    async (reportId, exportFormat, parameters) => {
      try {
        await generateReport(
          reportId,
          state.account.id,
          exportFormat,
          parameters,
          dispatch
        );
        dispatch({
          type: "ALERT",
          alert: {
            type: SUCCESS,
            msg: i18next.t("REPORT_GENERATION_STARTED"),
          },
        });
        setShowGenerationFlow(false);

        // Fetch recent jobs immediately to show the new pending job and start polling
        setTimeout(async () => {
          try {
            const recentJobs = await getRecentJobs(state.account.id, dispatch);
            const recentJobsList = Array.isArray(recentJobs) ? recentJobs : (recentJobs?.content || []);

            // Merge with existing jobs
            setJobs((prevJobs) => {
              const jobsMap = new Map(prevJobs.map(j => [j.jobId, j]));
              recentJobsList.forEach(recentJob => {
                jobsMap.set(recentJob.jobId, recentJob);
              });
              return Array.from(jobsMap.values());
            });

            // Start polling immediately if there's a pending job and polling is not already running
            const hasPendingInFetch = recentJobsList.some((job) => job.status === "PENDING");
            if (hasPendingInFetch && !intervalRef.current) {
              const interval = setInterval(async () => {
                try {
                  const recentJobs = await getRecentJobs(state.account.id, dispatch);
                  const recentJobsList = Array.isArray(recentJobs) ? recentJobs : (recentJobs?.content || []);

                  const hasPending = recentJobsList.some((job) => job.status === "PENDING");

                  if (!hasPending && intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                  }

                  setJobs((prevJobs) => {
                    const jobsMap = new Map(prevJobs.map(j => [j.jobId, j]));
                    recentJobsList.forEach(recentJob => {
                      jobsMap.set(recentJob.jobId, recentJob);
                    });
                    return Array.from(jobsMap.values());
                  });
                } catch (e) {
                  console.error("Error fetching recent jobs:", e);
                }
              }, 2000);
              intervalRef.current = interval;
            }
          } catch (e) {
            console.error("Error fetching recent jobs after generation:", e);
          }
        }, 500);
      } catch (e) {
        const msg =
          (e?.message?.error && e?.message?.error[0]?.code) ||
          "INTERNAL_ERROR";
        dispatch({ type: "ALERT", alert: { type: ERROR, msg } });
      }
    },
    [dispatch, state.account]
  );

  const handleDownload = useCallback(
    async (jobId) => {
      try {
        const resp = await getDownloadUrl(jobId, dispatch);
        if (resp?.downloadUrl) {
          window.open(resp.downloadUrl, "_blank");
        }
      } catch (e) {
        const msg =
          (e?.message?.error && e?.message?.error[0]?.code) ||
          "INTERNAL_ERROR";
        dispatch({ type: "ALERT", alert: { type: ERROR, msg } });
      }
    },
    [dispatch]
  );

  const handleDelete = useCallback(
    async (jobId) => {
      try {
        await deleteJob(jobId, dispatch);
        dispatch({
          type: "ALERT",
          alert: {
            type: SUCCESS,
            msg: i18next.t("REPORT_DELETED_SUCCESS"),
          },
        });
        fetchJobs();
      } catch (e) {
        const msg =
          (e?.message?.error && e?.message?.error[0]?.code) ||
          "INTERNAL_ERROR";
        dispatch({ type: "ALERT", alert: { type: ERROR, msg } });
      }
    },
    [dispatch, fetchJobs]
  );

  const getStatusChip = (status) => {
    const statusColors = {
      PENDING: "info",
      COMPLETED: "success",
      FAILED: "error",
    };

    return (
      <Chip
        label={i18next.t(`REPORT_STATUS_${status}`)}
        color={statusColors[status] || "default"}
        size="small"
      />
    );
  };

  const jobsColumns = [
    {
      field: "reportLabel",
      headerName: i18next.t("REPORT_NAME"),
      flex: 1,
      minWidth: 200,
    },
    {
      field: "status",
      headerName: i18next.t("REPORT_STATUS"),
      width: 150,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: "exportFormat",
      headerName: i18next.t("REPORT_FORMAT"),
      width: 100,
      renderCell: (params) => params.value?.toUpperCase() || "XLSX",
    },
    {
      field: "createdAt",
      headerName: i18next.t("REPORT_CREATED_AT"),
      width: 180,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD/MM/YYYY HH:mm") : "-",
    },
    {
      field: "completedAt",
      headerName: i18next.t("REPORT_COMPLETED_AT"),
      width: 180,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD/MM/YYYY HH:mm") : "-",
    },
    {
      field: "rowCount",
      headerName: i18next.t("REPORT_ROW_COUNT"),
      width: 120,
      renderCell: (params) => (params.value ? params.value.toLocaleString() : "-"),
    },
    {
      field: "fileSizeBytes",
      headerName: i18next.t("REPORT_FILE_SIZE"),
      width: 120,
      renderCell: (params) => {
        if (!params.value) return "-";
        const sizeInKB = (params.value / 1024).toFixed(2);
        const sizeInMB = (params.value / 1024 / 1024).toFixed(2);
        return sizeInMB > 1 ? `${sizeInMB} MB` : `${sizeInKB} KB`;
      },
    },
    {
      field: "actions",
      headerName: i18next.t("ACTIONS"),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "4px" }}>
          {params.row.status === "COMPLETED" && (
            <Tooltip title={i18next.t("DOWNLOAD")}>
              <IconButton
                size="small"
                onClick={() => handleDownload(params.row.jobId)}
                color="primary"
              >
                <Icon>download</Icon>
              </IconButton>
            </Tooltip>
          )}
          {params.row.status === "FAILED" && (
            <Tooltip title={params.row.errorMessage || i18next.t("ERROR")}>
              <IconButton size="small" color="error">
                <Icon>info</Icon>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={i18next.t("DELETE")}>
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.jobId)}
              color="error"
            >
              <Icon>delete</Icon>
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <ListLayout
      title="Exports"
      icon="drawer/exports_list"
      count={jobs.length}
      isLoading={loader}
      paginationType="NONE"
      filterField={
        !showGenerationFlow && <ReportsEmptyState onGenerate={handleGenerate} />
      }
    >
      {/* Generation Flow - shown when user clicks generate */}
      {showGenerationFlow && (
        <div style={{ padding: "24px" }}>
          <ReportGenerationFlow
            availableReports={availableReports}
            onSubmit={handleGenerationSubmit}
            onCancel={() => setShowGenerationFlow(false)}
          />
        </div>
      )}

      {/* Jobs List */}
      {jobs.length > 0 && !showGenerationFlow && (
        <>
          <style>
            {`
              @keyframes blink {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
              }
              .pending-row {
                animation: blink 1.5s ease-in-out infinite;
              }
            `}
          </style>
          <EADataGrid
            loading={loader}
            rows={jobs}
            columns={jobsColumns}
            getRowId={(row) => row.jobId}
            initialState={{
              sorting: {
                sortModel: [{ field: "createdAt", sort: "desc" }],
              },
            }}
            disableRowSelectionOnClick
            autoHeight
            getRowClassName={(params) =>
              params.row.status === "PENDING" ? "pending-row" : ""
            }
          />
        </>
      )}

      {/* Empty state when no jobs and not in generation flow */}
      {jobs.length === 0 && !showGenerationFlow && (
        <div style={{
          padding: "48px",
          textAlign: "center",
          color: "#666"
        }}>
          <Typography variant="body1">
            Aucun export pour le moment
          </Typography>
        </div>
      )}
    </ListLayout>
  );
}

export default ReportsDataTable;
